import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {WorkforceCultureFitPercentRow} from './WorkforceCultureFitPercentRow';
import {culturalAlignmentColors} from './WorkforceProfileDetail.colors';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileCulturalFitSummary} from '../../types/WorkforceProfileTypes';
import {COMPANY_MARKER_COLOR, PROFILE_MARKER_COLOR} from '../../utils/WorkforceProfileDetail/workforceCulturalAlignmentUtils';

export interface Props {
  cultureTypeName: string | null;
  cultureFit: WorkforceProfileCulturalFitSummary | null;
}

export const WorkforceCultureTypeOverviewCard = ({cultureTypeName, cultureFit}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.culturalFitAnalysis'});

  if (!cultureTypeName && !cultureFit) {
    return null;
  }

  const fitScore = cultureFit?.fitScore;
  const companyFit = cultureFit?.averageCompanyEmployeeFit;
  const applicantAvgFit = cultureFit?.averageCompanyApplicantFit;
  const profileMatchesAverage =
    fitScore != null &&
    applicantAvgFit != null &&
    Math.round(fitScore) === Math.round(applicantAvgFit);

  return (
    <Box
      sx={{
        mb: 2,
        p: '1.25rem 1.5rem',
        bgcolor: 'background.paper',
        border: `1px solid ${orangeColors[5]}`,
        borderRadius: '12px',
      }}
    >
      {cultureTypeName && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5}}>
          <Box
            component='span'
            sx={{
              fontSize: 11,
              fontWeight: 500,
              px: '10px',
              py: '3px',
              borderRadius: '20px',
              bgcolor: culturalAlignmentColors.cultureTypeChip.bgcolor,
              color: culturalAlignmentColors.cultureTypeChip.color,
              letterSpacing: '0.03em',
            }}
          >
            {t('cultureTypeChip')}
          </Box>
          <Typography sx={{fontSize: 14, fontWeight: 500, color: 'text.primary'}}>
            {cultureTypeName}
          </Typography>
        </Box>
      )}

      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.75}}>
        {companyFit != null && (
          <WorkforceCultureFitPercentRow
            title={t('avgEmployeeFit')}
            description={t('avgEmployeeFitDescription')}
            score={companyFit}
            color={COMPANY_MARKER_COLOR}
          />
        )}
        {applicantAvgFit != null && (
          <WorkforceCultureFitPercentRow
            title={t('avgApplicantFit')}
            description={t('avgApplicantFitDescription')}
            score={applicantAvgFit}
            color={culturalAlignmentColors.applicantAvgMarker}
          />
        )}
        {fitScore != null && (
          <WorkforceCultureFitPercentRow
            title={t('fitScore')}
            description={t('thisProfileDescription')}
            score={fitScore}
            color={PROFILE_MARKER_COLOR}
            barHeight={8}
            borderedTop
            footer={
              (profileMatchesAverage || companyFit != null) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 0.5,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {profileMatchesAverage && (
                    <Typography sx={{fontSize: 11, color: 'text.disabled'}}>
                      {t('profileSameAsAverage')}
                    </Typography>
                  )}
                  {companyFit != null && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: 'text.disabled',
                        ml: profileMatchesAverage ? 'auto' : 0,
                      }}
                    >
                      {t('companyBenchmark', {percent: Math.round(companyFit)})}
                    </Typography>
                  )}
                </Box>
              )
            }
          />
        )}
      </Box>
    </Box>
  );
};
